// CloudKeeper Org Services Portal — GuardDuty API client.
// Uses portalFetch from portalApi.js (JWT stored as portal_token).
// Mirrors securityhubApi.js: every call resolves to the {success,data} envelope.
import { portalFetch } from './portalApi';

// --- config ---
export const getGuardDutyConfig = () =>
  portalFetch('/api/v1/securityhub/guardduty/config');

export const enableGuardDuty = (body) =>
  portalFetch('/api/v1/securityhub/guardduty/config', {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const disableGuardDuty = () =>
  portalFetch('/api/v1/securityhub/guardduty/config', { method: 'DELETE' });

export const updateFeatures = (features) =>
  portalFetch('/api/v1/securityhub/guardduty/config/features', {
    method: 'PUT',
    body: JSON.stringify({ features }),
  });

export const updateGDRegions = (linkingRegions) =>
  portalFetch('/api/v1/securityhub/guardduty/config/regions', {
    method: 'PUT',
    body: JSON.stringify({ linkingRegions }),
  });

// --- members ---
export const listGDMembers = () =>
  portalFetch('/api/v1/securityhub/guardduty/members');

export const addGDMembers = (accountIds, memberEmails) =>
  portalFetch('/api/v1/securityhub/guardduty/members', {
    method: 'POST',
    body: JSON.stringify({ accountIds, memberEmails }),
  });

// --- member emails ---
// Each account's root-email lookup is a per-account ops-service round-trip on the
// backend. Resolving a large selection in one request can exceed the gateway
// timeout (504), so split into small batches and fire them sequentially — the next
// batch starts as soon as the previous returns. Each request stays well under the
// timeout regardless of how many accounts are selected. Results are merged into the
// same { data: { results } } shape callers already expect.
const RESOLVE_CHUNK_SIZE = 5;

export const resolveMemberEmails = async (accountIds) => {
  const ids = accountIds || [];
  const results = [];
  for (let i = 0; i < ids.length; i += RESOLVE_CHUNK_SIZE) {
    const chunk = ids.slice(i, i + RESOLVE_CHUNK_SIZE);
    // eslint-disable-next-line no-await-in-loop -- intentional: one batch at a time
    const res = await portalFetch('/api/v1/securityhub/guardduty/member-emails/resolve', {
      method: 'POST',
      body: JSON.stringify({ accountIds: chunk }),
    });
    results.push(...(res?.data?.results || []));
  }
  return { data: { results } };
};

export const removeGDMember = (accountId) =>
  portalFetch(`/api/v1/securityhub/guardduty/members/${encodeURIComponent(accountId)}`, {
    method: 'DELETE',
  });

export const retryMember = (accountId) =>
  portalFetch(`/api/v1/securityhub/guardduty/members/${encodeURIComponent(accountId)}/retry`, {
    method: 'POST',
  });

// --- findings ---
export const listGuardDutyFindings = (filters = {}) => {
  const qs = new URLSearchParams();
  // Repeated array params with NO "[]" suffix — mirror securityhubApi.listFindings.
  if (filters.severity?.length) qs.set('severity', filters.severity.join(','));
  if (filters.workflowStatus?.length) qs.set('workflowStatus', filters.workflowStatus.join(','));
  if (filters.complianceStatus?.length) qs.set('complianceStatus', filters.complianceStatus.join(','));
  if (filters.account?.length) qs.set('account', filters.account.join(','));
  if (filters.search) qs.set('search', filters.search);
  if (filters.cursor) qs.set('cursor', filters.cursor);
  if (filters.limit) qs.set('limit', String(filters.limit));
  const q = qs.toString();
  return portalFetch(`/api/v1/securityhub/guardduty/findings${q ? `?${q}` : ''}`);
};

export const getGuardDutyFinding = (id) =>
  portalFetch(`/api/v1/securityhub/guardduty/findings/${encodeURIComponent(id)}`);

export const updateGDWorkflow = ({ findingIds, archive }) =>
  portalFetch('/api/v1/securityhub/guardduty/findings/workflow', {
    method: 'POST',
    body: JSON.stringify({ findingIds, archive }),
  });

// --- refresh ---
export const triggerGDRefresh = () =>
  portalFetch('/api/v1/securityhub/guardduty/refresh', { method: 'POST' });

export const getGDRefreshJob = (id) =>
  portalFetch(`/api/v1/securityhub/guardduty/refresh/${encodeURIComponent(id)}`);

// Currently running refresh job for this tenant (data: null when none) — lets
// the dashboard re-attach its progress bar after navigation or a page reload.
export const getGDActiveRefreshJob = () =>
  portalFetch('/api/v1/securityhub/guardduty/refresh/active');

// --- summary ---
export const getThreatSummary = () =>
  portalFetch('/api/v1/securityhub/guardduty/summary');

// --- role permissions ("Fix roles") ---
// Prism keeps a scoped inline policy on each enrolled account's switch role. The
// scan is async: POST starts (or re-joins) a job, GET polls it. Fix is
// fire-and-forget — re-run a scan to see the outcome.
export const startGDRolesScan = () =>
  portalFetch('/api/v1/securityhub/guardduty/roles/status', { method: 'POST' });

export const getGDRolesJob = (jobId) =>
  portalFetch(`/api/v1/securityhub/guardduty/roles/status/${encodeURIComponent(jobId)}`);

export const fixGDRoles = () =>
  portalFetch('/api/v1/securityhub/guardduty/roles/fix', { method: 'POST' });
